import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { auth, db, functions } from '../utils/firebase-config';
import { httpsCallable } from 'firebase/functions';
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signInWithPopup,
  linkWithPopup,
  getIdTokenResult,
  getRedirectResult,
  GoogleAuthProvider,
  signOut,
  sendPasswordResetEmail,
  sendEmailVerification,
  onAuthStateChanged,
  updateProfile,
  fetchSignInMethodsForEmail,
} from 'firebase/auth';
import { trackEngagement, setUserProperties } from '../utils/analytics';
import { doc, setDoc, getDoc, serverTimestamp, runTransaction, onSnapshot } from 'firebase/firestore';
import { getCurrencySymbol } from '../utils/currency';
import { hasPaidAccess, FREE_PLAN_LIMITS } from '../utils/entitlements';
import { RESERVED_PATHS } from '../utils/reservedPaths';

const AuthContext = createContext(null);

// SoapLab sends its own branded account emails. If that fails for any reason other than a
// rate limit or a bad address, Firebase's default email is sent instead so nobody gets stuck.
const sendAccountEmail = async (name, data, fallback) => {
  try {
    await httpsCallable(functions, name)(data);
  } catch (error) {
    if (['functions/resource-exhausted', 'functions/invalid-argument', 'functions/unauthenticated'].includes(error.code)) throw error;
    console.warn(`${name} failed; sending Firebase's email instead:`, error.message);
    await fallback();
  }
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [adminAccess, setAdminAccess] = useState(false);
  const [, setEntitlementTick] = useState(0);

  // Fetch user profile from Firestore
  const fetchUserProfile = useCallback(async (uid) => {
    try {
      const userDoc = await getDoc(doc(db, 'users', uid));
      if (userDoc.exists()) {
        const data = userDoc.data();
        if (auth.currentUser?.uid === uid) setUserProfile(data);
        return data;
      }
    } catch (error) {
      console.error('Error fetching user profile:', error);
    }
    return null;
  }, []);

  const ensurePublicProfile = useCallback(async (firebaseUser, profileData) => {
    if (!firebaseUser) return;
    try {
      const publicRef = doc(db, 'publicProfiles', firebaseUser.uid);
      const publicSnap = await getDoc(publicRef);
      if (!publicSnap.exists()) {
        await setDoc(publicRef, {
          uid: firebaseUser.uid,
          displayName: profileData?.displayName || firebaseUser.displayName || 'Soap maker',
          photoURL: profileData?.photoURL || firebaseUser.photoURL || '',
          username: profileData?.username || '',
          usernameLower: profileData?.username ? profileData.username.toLowerCase() : '',
          updatedAt: serverTimestamp(),
        }, { merge: true });
      }
    } catch (error) {
      console.error('Error ensuring public profile:', error);
    }
  }, []);

  // Create or update user profile in Firestore
  const createUserProfile = useCallback(async (uid, data) => {
    try {
      const userRef = doc(db, 'users', uid);
      const publicRef = doc(db, 'publicProfiles', uid);
      const existingDoc = await getDoc(userRef);
      
      if (existingDoc.exists()) {
        // Update existing profile
        await setDoc(userRef, {
          ...existingDoc.data(),
          ...data,
          updatedAt: serverTimestamp(),
        }, { merge: true });
      } else {
        // Create new profile
        await setDoc(userRef, {
          ...data,
          uid,
          plan: 'free', // Default plan
          planExpiry: null,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          // Usage limits for free plan
          limits: {
            maxRecipes: 10,
            maxIngredients: 30,
            maxBatches: 5,
          }
        });
      }

      // Create/update public profile (default public)
      await setDoc(publicRef, {
        uid,
        displayName: data.displayName || existingDoc.data()?.displayName || 'Soap maker',
        photoURL: data.photoURL || existingDoc.data()?.photoURL || '',
        username: data.username ?? existingDoc.data()?.username ?? '',
        usernameLower: (data.username ?? existingDoc.data()?.username ?? '').toLowerCase(),
        updatedAt: serverTimestamp(),
      }, { merge: true });
      
      await fetchUserProfile(uid);
    } catch (error) {
      console.error('Error creating user profile:', error);
      throw error;
    }
  }, [fetchUserProfile]);

  // Follow profile/role changes so a verified payment or expiry updates all gates.
  useEffect(() => {
    let unsubscribeProfile = () => {};
    let unsubscribeAdmins = () => {};
    let revision = 0;
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      const currentRevision = ++revision;
      unsubscribeProfile();
      unsubscribeAdmins();
      setUser(firebaseUser || null);
      setUserProfile(null);
      setAdminAccess(false);
      if (!firebaseUser) { setLoading(false); return; }
      setLoading(true);
      unsubscribeProfile = onSnapshot(doc(db, 'users', firebaseUser.uid), snapshot => {
        if (revision !== currentRevision) return;
        const profile = snapshot.exists() ? snapshot.data() : null;
        setUserProfile(profile);
        setUserProperties(firebaseUser.uid, hasPaidAccess(profile) ? 'pro' : 'free');
        setLoading(false);
      }, error => { console.error('Unable to load account:', error); setLoading(false); });
      try {
        const token = await getIdTokenResult(firebaseUser);
        if (revision !== currentRevision) return;
        const trustedAdmin = firebaseUser.uid === 'wGe22UwE1FQN4qPcEGaVkdG3qdD2' || token.claims.admin === true;
        setAdminAccess(trustedAdmin);
        unsubscribeAdmins = onSnapshot(doc(db, 'settings', 'admins'), snapshot => {
          if (revision === currentRevision) setAdminAccess(trustedAdmin || snapshot.data()?.adminUids?.[firebaseUser.uid] === true);
        }, () => setAdminAccess(trustedAdmin));
        const profile = await fetchUserProfile(firebaseUser.uid);
        if (revision === currentRevision) await ensurePublicProfile(firebaseUser, profile);
      } catch (error) { console.error('Unable to load account settings:', error); setLoading(false); }
    });
    const expiryTimer = setInterval(() => setEntitlementTick(value => value + 1), 60000);

    // Check for redirect result on mount
    const handleRedirectResult = async () => {
      try {
        const result = await getRedirectResult(auth);
        if (result) {
          // Create/update user profile after redirect
          await createUserProfile(result.user.uid, {
            email: result.user.email,
            displayName: result.user.displayName,
            photoURL: result.user.photoURL,
            authProvider: 'google',
          });
          trackEngagement.login('google');
        }
      } catch (error) {
        console.error('Redirect result error:', error);
      }
    };

    handleRedirectResult();

    return () => { revision++; unsubscribe(); unsubscribeProfile(); unsubscribeAdmins(); clearInterval(expiryTimer); };
  }, [createUserProfile, ensurePublicProfile, fetchUserProfile]);

  // Sign up with email and password
  const signUp = async (email, password, displayName) => {
    try {
      const result = await createUserWithEmailAndPassword(auth, email, password);
      
      // Update display name
      await updateProfile(result.user, { displayName });
      
      // The account exists now, so a failed email must not fail sign-up; the
      // verification page can send it again.
      try {
        await sendAccountEmail('requestEmailVerification', {}, () => sendEmailVerification(result.user));
      } catch (error) {
        console.error('Verification email error:', error);
      }
      
      // Create user profile in Firestore
      await createUserProfile(result.user.uid, {
        email: result.user.email,
        displayName,
        authProvider: 'email',
      });
      
      trackEngagement.signUp('email');
      return result.user;
    } catch (error) {
      console.error('Sign up error:', error);
      throw error;
    }
  };

  // Sign in with email and password
  const signIn = async (email, password) => {
    try {
      const result = await signInWithEmailAndPassword(auth, email, password);
      trackEngagement.login('email');
      return result.user;
    } catch (error) {
      console.error('Sign in error:', error);
      throw error;
    }
  };

  // Check what sign-in methods exist for an email
  const getSignInMethodsForEmail = async (email) => {
    try {
      const methods = await fetchSignInMethodsForEmail(auth, email);
      return methods;
    } catch (error) {
      console.error('Error fetching sign-in methods:', error);
      return [];
    }
  };

  // Link Google account to existing email/password account
  const linkGoogleAccount = async () => {
    if (!user) throw new Error('Must be logged in to link accounts');
    
    try {
      const provider = new GoogleAuthProvider();
      const result = await linkWithPopup(user, provider);
      
      // Update profile with Google info
      await createUserProfile(user.uid, {
        photoURL: result.user.photoURL,
        googleLinked: true,
      });
      
      return result;
    } catch (error) {
      console.error('Error linking Google account:', error);
      throw error;
    }
  };

  // Sign in with Google in a pop-up window, on phones and computers alike. The redirect method
  // breaks in browsers that block third-party storage (Safari, newer Chrome) because Google
  // sign-in runs on portfolio-b4af2.firebaseapp.com rather than soaplab.in.
  // Returns null when the person simply closes the Google window.
  const signInWithGoogle = async () => {
    // Google refuses sign-in inside the browsers built into Instagram, Facebook and similar apps.
    if (/FBAN|FBAV|Instagram|Line\/|; wv\)/i.test(navigator.userAgent)) {
      const error = new Error('Google sign-in doesn’t work inside this app’s browser. Open soaplab.in in Chrome or Safari and sign in there.');
      error.code = 'auth/in-app-browser';
      throw error;
    }
    try {
      const result = await signInWithPopup(auth, new GoogleAuthProvider());
      // One email is one account: Firebase signs Google users into the existing account for
      // that email, so this only fills in profile details.
      await createUserProfile(result.user.uid, {
        email: result.user.email,
        displayName: result.user.displayName,
        photoURL: result.user.photoURL,
        authProvider: 'google',
        googleLinked: true,
      });
      trackEngagement.login('google');
      return result.user;
    } catch (error) {
      if (['auth/popup-closed-by-user', 'auth/cancelled-popup-request', 'auth/user-cancelled'].includes(error.code)) return null;
      console.error('Google sign in error:', error);
      throw error;
    }
  };

  // Sign out
  const logOut = async () => {
    try {
      await signOut(auth);
      setUser(null);
      setUserProfile(null);
    } catch (error) {
      console.error('Sign out error:', error);
      throw error;
    }
  };

  // Reset password
  const resetPassword = async (email) => {
    try {
      await sendAccountEmail('requestPasswordReset', { email }, () => sendPasswordResetEmail(auth, email));
    } catch (error) {
      console.error('Password reset error:', error);
      throw error;
    }
  };

  // Resend verification email
  const resendVerificationEmail = async () => {
    if (!auth.currentUser) throw new Error('No user logged in');
    if (auth.currentUser.emailVerified) throw new Error('Email already verified');
    const currentUser = auth.currentUser;
    await sendAccountEmail('requestEmailVerification', {}, () => sendEmailVerification(currentUser));
  };

  const hasActiveSubscription = () => !userProfile || userProfile.plan === 'free' ? false : hasPaidAccess(userProfile);
  const getPlanLimits = () => hasPaidAccess(userProfile)
    ? { maxRecipes: -1, maxIngredients: -1, maxBatches: -1 }
    : FREE_PLAN_LIMITS;
  const isPro = () => hasPaidAccess(userProfile);
  // Roles never come from the user-editable profile document or email alone.
  const isAdmin = () => Boolean(user && adminAccess);

  const normalizeUsername = (value) => value.trim().toLowerCase();
  const isValidUsername = (value) => /^[a-z0-9_]{3,20}$/.test(value);
  const reservedUsernames = RESERVED_PATHS;

  const updateProfileDetails = async ({ displayName, photoURL, username, bio }) => {
    if (!user) throw new Error('Must be logged in');
    const userRef = doc(db, 'users', user.uid);
    const publicRef = doc(db, 'publicProfiles', user.uid);
    const previousUsername = (userProfile?.username || '').toLowerCase();

    const nextDisplayName = displayName?.trim() || user.displayName || user.email || 'User';
    const nextPhotoURL = photoURL?.trim() || '';
    const nextUsername = username !== undefined ? normalizeUsername(username || '') : previousUsername;
    // A short line about the soap someone makes, shown on their profile.
    const nextBio = bio !== undefined ? String(bio || '').trim().slice(0, 280) : (userProfile?.bio || '');

    if (nextUsername && reservedUsernames.has(nextUsername)) {
      throw new Error('That username is reserved.');
    }
    if (nextUsername && !isValidUsername(nextUsername)) {
      throw new Error('Username must be 3-20 characters: letters, numbers, underscore only.');
    }

    if (nextUsername !== previousUsername) {
      await runTransaction(db, async (tx) => {
        if (nextUsername) {
          const newRef = doc(db, 'usernames', nextUsername);
          const newSnap = await tx.get(newRef);
          if (newSnap.exists() && newSnap.data().uid !== user.uid) {
            throw new Error('That username is already taken.');
          }
          tx.set(newRef, { uid: user.uid, username: nextUsername });
        }
        if (previousUsername) {
          const oldRef = doc(db, 'usernames', previousUsername);
          tx.delete(oldRef);
        }
      });
    }

    await updateProfile(user, {
      displayName: nextDisplayName,
      photoURL: nextPhotoURL || null,
    });

    await setDoc(userRef, {
      displayName: nextDisplayName,
      photoURL: nextPhotoURL,
      username: nextUsername,
      bio: nextBio,
      updatedAt: serverTimestamp(),
    }, { merge: true });

    await setDoc(publicRef, {
      displayName: nextDisplayName,
      photoURL: nextPhotoURL,
      username: nextUsername,
      usernameLower: nextUsername,
      bio: nextBio,
      updatedAt: serverTimestamp(),
    }, { merge: true });

    await fetchUserProfile(user.uid);
  };

  const currencyCode = userProfile?.currencyCode || 'INR';

  const updateCurrencyPreference = async (nextCurrencyCode) => {
    if (!user) throw new Error('Must be logged in');
    const userRef = doc(db, 'users', user.uid);
    await setDoc(userRef, {
      currencyCode: nextCurrencyCode,
      currencySymbol: getCurrencySymbol(nextCurrencyCode),
      updatedAt: serverTimestamp(),
    }, { merge: true });
    await fetchUserProfile(user.uid);
  };

  const value = {
    user,
    userProfile,
    loading,
    signUp,
    signIn,
    signInWithGoogle,
    logOut,
    resetPassword,
    resendVerificationEmail,
    hasActiveSubscription,
    getPlanLimits,
    fetchUserProfile,
    linkGoogleAccount,
    getSignInMethodsForEmail,
    isPro,
    isAdmin,
    updateProfileDetails,
    currencyCode,
    currencySymbol: getCurrencySymbol(currencyCode),
    updateCurrencyPreference,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
